import { useRef, useState } from 'react'
import { debounce } from 'lodash'
import { useCookies } from 'react-cookie'
import queryString from 'query-string'

import requestService from 'services/request.service'

import { DecksWithCardResponseType, DeckWithCardType } from 'services/apiTypes/deck.types'
import { DeckTagRelation } from 'services/apiTypes/deckTag.types'

import AsyncDropdown, { Option } from 'components/elements/AsyncDropdown'

import styles from './deckTypeahead.module.scss'

type Props = {
  onDeckSelected: (deckId: number, tags: DeckTagRelation[]) => void
  header?: React.ReactNode
  placeholder?: string
  className?: string
}

const DeckTypeahead = ({ onDeckSelected, header, placeholder, className }: Props) => {
  const [{ tbId: userId }] = useCookies(['tbId'])

  const [_selectedDeckId, setSelectedDeckId] = useState<number | null>(null)
  const [searchedDeckName, setSearchedDeckName] = useState('')
  const [dropdownLoading, setDropdownLoading] = useState(false)
  const [decksWithCard, setDecksWithCard] = useState<Array<DeckWithCardType>>([])

  const fetchDecks = async (deckName?: string) => {
    const params: Record<string, any> = {}

    if (deckName) params.deckName = deckName

    const requestUrl = `/api/users/${userId}/decksWithCard/?${queryString.stringify(params)}`

    requestService
      .get<DecksWithCardResponseType>(requestUrl)
      .then(data => setDecksWithCard(data.decks))
      .finally(() => setDropdownLoading(false))
  }

  const debouncedFetchDecks = useRef(debounce(fetchDecks, 500)).current
  const deckSearchInputRef = useRef<any>(null)

  const handleSearchForDecks = (deckName?: string) => {
    setSearchedDeckName(deckName || '')
    setDropdownLoading(true)

    debouncedFetchDecks(deckName)
  }

  const options: Option[] = decksWithCard.map(deck => ({
    label: deck.name,
    id: `${deck.id}`,
    value: deck.id,
  }))

  return (
    <>
      {header && <label className={styles.header}>{header}</label>}
      <AsyncDropdown
        ref={deckSearchInputRef}
        placeholder={placeholder || 'Select a deck...'}
        loading={dropdownLoading}
        options={options}
        onChange={id => {
          const selectedDeck = decksWithCard.find(o => `${o.id}` === `${id}`)

          if (!selectedDeck) return

          setSelectedDeckId(selectedDeck.id)
          setSearchedDeckName(selectedDeck.name || '')

          onDeckSelected(selectedDeck.id, selectedDeck.tags || [])
        }}
        onSearchChange={handleSearchForDecks}
        onFocus={() => {
          deckSearchInputRef?.current?.select()

          if (!options.length) handleSearchForDecks()
        }}
        value={searchedDeckName}
        triggerClassName={styles.deckSearchDropdown}
        containerClassName={className}
      />
    </>
  )
}

export default DeckTypeahead
